<template>
  <v-card class="mb-1 px-1" :style="withBorder ? '' : 'border: none'" :disabled="loading" outlined>
    <v-card-title v-if="filterTitle.length" style="font-size: 20px">{{ filterTitle }}</v-card-title>

    <v-card-text>
      <slot name="before-filters" :updateDynamicFilter="updateDynamicFilter"></slot>

      <v-row>
        <slot name="inline-filter" />

        <template v-for="(field, index) in filters">
          <!-- Select -->
          <v-col v-if="field.type === 'v-select'" :key="`select-${index}`" cols="12" :md="colunasCampo(field)" class="py-0">
            <v-select
              v-model="filterSelected[field.model]"
              :label="field.label"
              :items="field.items"
              :item-text="field.itemText"
              :item-value="field.itemValue"
              :multiple="field.multiple || false"
              :outlined="field.outlined || false"
              clearable
              dense
              :loading="loading"
              :disabled="loading || validateDependentValues(field.depends)"
              @change="handleFilterFieldChange(field)"
            />
          </v-col>

          <!-- Autocomplete -->
          <v-col
            v-else-if="field.type === 'v-autocomplete'"
            :key="`autocomplete-${index}`"
            cols="12"
            :md="colunasCampo(field)"
            class="py-0"
          >
            <v-autocomplete
              v-model="filterSelected[field.model]"
              :label="field.label"
              :items="field.items"
              :item-text="field.itemText"
              :item-value="field.itemValue"
              :multiple="field.multiple || false"
              :outlined="field.outlined || false"
              :filter="customFilterWithParam(field.model)"
              clearable
              dense
              :loading="loading"
              :disabled="loading || validateDependentValues(field.depends)"
              @change="handleFilterFieldChange(field)"
            />
          </v-col>

          <!-- Autocomplete (assíncrono) -->
          <v-col
            v-else-if="field.type === 'v-autocomplete-async'"
            :key="`autocomplete-async-${index}`"
            cols="12"
            :md="colunasCampo(field)"
            class="py-0"
          >
            <autocomplete-assincrono
              :ref="field.model"
              v-model="filterSelected[field.model]"
              :placeholder="field.placeholder"
              :service="service"
              :service-method="field.serviceMethod || 'asyncAutocompleteSearch'"
              query-param-name="q"
              :item-text="field.itemText || 'text'"
              :item-value="field.itemValue || 'value'"
              :label="field.label"
              :min-chars="0"
              :debounce-ms="400"
              :clearable="true"
              :dense="true"
              :outlined="true"
              :multiple="field.multiple || false"
              :extraParams="{ ...prepareFilters(), ...filterSlotProp }"
              :disabled="validateDependentValues(field.depends)"
              @change="handleFilterFieldChange(field)"
              @results="field.items = $event"
            />
          </v-col>

          <!-- Text Field -->
          <v-col
            v-else-if="field.type === 'v-text-field'"
            :key="`text-field-${index}`"
            cols="12"
            :md="colunasCampo(field)"
            class="py-0"
          >
            <v-text-field
              v-model="filterSelected[field.model]"
              :label="field.label"
              :placeholder="field.placeholder"
              :outlined="field.outlined || false"
              :disabled="validateDependentValues(field.depends)"
              clearable
              dense
            />
          </v-col>

          <!-- Textarea -->
          <v-col v-else-if="field.type === 'v-textarea'" :key="`textarea-${index}`" cols="12" :md="colunasCampo(field)" class="py-0">
            <v-textarea
              v-model="filterSelected[field.model]"
              :label="`${field.label} - ${countNonEmptyLines(field.model)}`"
              :rows="field.rows || 4"
              :outlined="field.outlined || false"
              :disabled="validateDependentValues(field.depends)"
              clearable
            />
          </v-col>
        </template>
      </v-row>

      <!-- Slot para conteúdo customizado após os filtros -->
      <slot name="after-filters" :updateDynamicFilter="updateDynamicFilter"></slot>

      <v-row>
        <v-col cols="12" md="6" class="d-flex justify-center justify-md-start pl-md-0">
          <v-btn
            color="#D7D7D7"
            class="ma-0 ml-md-3"
            style="width: 130px; color: #757575"
            :disabled="loading"
            @click="resetFilters"
          >
            Resetar
          </v-btn>
        </v-col>

        <v-col cols="12" md="6" class="d-flex justify-center justify-md-end pr-md-0">
          <v-btn
            color="primary"
            class="ma-0 mr-md-3"
            style="width: 130px"
            :disabled="loading || validateDependentValues(buttonDependencies)"
            @click="applyFilters"
          >
            Filtrar
          </v-btn>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
  import filterMixin from '@/mixins/filterMixin';

  export default {
    name: 'FiltrosDinamicos',

    components: {
      AutocompleteAssincrono: () => import('@/components/general/inputs/AutocompleteAssincrono.vue')
    },

    mixins: [filterMixin],

    props: {
      filterTitle: {
        type: String,
        default: 'Filtros'
      },
      filtersProp: {
        type: Object,
        required: true
      },
      filterSlotProp: {
        type: Object,
        default: () => ({})
      },
      service: {
        type: Object,
        required: true
      },
      serviceMethod: {
        type: String,
        default: 'getFilters'
      },
      loadingProp: {
        type: Boolean,
        default: false
      },
      withBorder: {
        type: Boolean,
        default: true
      },
      buttonDependencies: {
        type: Array,
        default: () => []
      },
      inlineFilterCols: {
        type: Object,
        default: () => ({})
      }
    },
    emits: ['applyFilters', 'resetFilters'],

    data() {
      return {
        loading: false,
        filterSelected: {},
        filters: {},
        allFilterSelected: {},
        alteredFilters: {},
        filterSearch: {}
      };
    },

    mounted() {
      this.runInitialization();
    },

    methods: {
      initializeFilters() {
        this.filters = JSON.parse(JSON.stringify(this.filtersProp));

        Object.entries(this.filtersProp).forEach(([key, field]) => {
          this.$set(this.filterSelected, key, field.multiple ? [] : '');
          this.$set(this.allFilterSelected, key, false);
          this.$set(this.alteredFilters, key, false);
          this.$set(this.filterSearch, key, []);
        });
      },

      async fetchFilters(slotFilters = {}) {
        this.loading = true;

        if (Object.keys(slotFilters).length === 0 && this.filterSlotProp) {
          slotFilters = { ...this.filterSlotProp };
        }

        try {
          const method = this.service[this.serviceMethod];
          if (typeof method !== 'function') {
            throw new Error(`Método '${this.serviceMethod}' não encontrado no service`);
          }

          const response = await method({ ...this.prepareFilters(), ...slotFilters });
          const data = response.data || {};

          for (const [key, rawValues] of Object.entries(data)) {
            if (!this.filters[key]) continue;

            const values = Array.isArray(rawValues) ? [...rawValues] : [];
            if (this.filters[key].multiple && values.length && !values.includes('SELECIONAR TUDO')) {
              values.unshift('SELECIONAR TUDO');
            }

            this.$set(this.filters[key], 'items', values);
            const itemText = this.filters[key].itemText;
            const searchableValues = values.map((value) =>
              itemText && value && typeof value === 'object' ? value[itemText] : value
            );
            this.$set(this.filterSearch, key, searchableValues);

            if (!(key in this.allFilterSelected)) {
              this.$set(this.allFilterSelected, key, false);
            }
          }
        } catch (error) {
          console.error('Erro ao buscar filtros:', error);
          this.$toast.error('Erro ao buscar os filtros.', '', {
            position: 'topRight'
          });
        } finally {
          this.loading = false;
        }
      },

      prepareFilters() {
        const processed = {};

        Object.entries(this.filterSelected).forEach(([key, value]) => {
          const field = Object.values(this.filters).find((f) => f.model === key);

          if (Array.isArray(value)) {
            processed[key] = value.map((item) => this.obterValorItem(field, item)).filter((item) => item !== 'SELECIONAR TUDO');
          } else if (field.type === 'v-textarea') {
            processed[key] = value ? value.split(/\r?\n/).filter(Boolean) : [];
          } else {
            processed[key] = value;
          }
        });

        return processed;
      },

      applyFilters() {
        const processed = this.prepareFilters();

        this.$emit('applyFilters', processed);
      },

      countNonEmptyLines(model = '') {
        const value = this.filterSelected[model] || '';
        return typeof value === 'string' ? value.split(/\r?\n/).filter(Boolean).length : 0;
      },

      async resetFilters() {
        Object.values(this.filters).forEach((field) => {
          if (this.filters[field.model].type === 'v-autocomplete-async') {
            const ref = this.$refs[field.model];
            const componente = Array.isArray(ref) ? ref[0] : ref;
            componente.clear();
          }

          this.$set(this.filterSelected, field.model, field.multiple ? [] : '');
          this.$set(this.allFilterSelected, field.model, false);
          this.$set(this.alteredFilters, field.model, false);
        });

        this.$emit('resetFilters');

        await this.$nextTick();
        await this.fetchFilters();
      },

      async updateDynamicFilter(key, slotFilters = {}) {
        if (this.alteredFilters[key] || key === '') {
          await this.fetchFilters(slotFilters);
          if (key !== '') this.alteredFilters[key] = false;

          Object.keys(this.filters).forEach((filterKey) => {
            if (this.filters[filterKey].type === 'v-autocomplete-async') {
              const ref = this.$refs[filterKey];
              const componente = Array.isArray(ref) ? ref[0] : ref;
              componente.performSearch('');
              return;
            }

            const selectedItems = this.filterSelected[filterKey];
            const availableItems = this.filters[filterKey].items;

            this.filterArray(selectedItems, availableItems, filterKey);
          });
        }
      },

      runInitialization() {
        this.initializeFilters();
        this.fetchFilters();
      },

      async handleFilterFieldChange(field) {
        const model = field.model;
        const selected = this.filterSelected[model];

        // Executa selectAll quando o campo é múltiplo
        if (field.multiple) {
          this.selectAll(model, selected, this.valoresDoFiltro(field));
        }

        // Marca o filtro como alterado
        this.alteredFilters[model] = true;

        await this.updateDynamicFilter(model);
      },

      obterValorItem(field, item) {
        if (!item || typeof item !== 'object') return item;

        const itemValue = (field && field.itemValue) || 'value';

        return item[itemValue] !== undefined ? item[itemValue] : item.value;
      },

      valoresDoFiltro(field) {
        return (field.items || []).map((item) => this.obterValorItem(field, item));
      },

      colunasCampo(field) {
        if (this.inlineFilterCols[field.model]) return this.inlineFilterCols[field.model];

        if (field.cols) return field.cols;

        return field.type === 'v-textarea' ? 12 : 6;
      },

      validateDependentValues(keys = []) {
        if (!keys || keys.length === 0) return false;

        return !keys.every((key) => {
          const value = this.filterSelected[key];

          if (typeof value === 'string') {
            return value.trim().length > 0;
          }

          if (Array.isArray(value)) {
            return value.length > 0;
          }

          return false;
        });
      }
    },

    watch: {
      filtersProp: {
        deep: true,
        handler() {
          this.initializeFilters();
        }
      },

      loadingProp: {
        immediate: true,
        handler(newVal) {
          this.loading = newVal;
        }
      }
    }
  };
</script>
